/**
 * `actor.blocksExits` desugared — D233 (actors round C; `docs/design/actors.md`
 * §5).
 *
 * @remarks
 * The general primitive is the lock's OBSTRUCTION arm, `blockedWhen`: a
 * condition that holds an exit shut for as long as it reads true and that
 * nothing in the unlock ledger cuts. "The guard stands in the doorway" is
 * sugar for a lock whose obstruction is `actor_present { guard }` (and, for a
 * foe, D244: not `actor_defeated`), and that
 * is what it is rewritten into here, at load, BEFORE semantic validation —
 * exactly as `room.isCheckpoint` becomes its trigger (D142) — so the
 * synthesized lock is validated, walked by the producibility check and
 * evaluated like an authored one, and the engine never learns the word
 * `blocksExits`. A door a guard stands in front of and a door that needs a
 * keycard are the same lock with different keys; a door that needs both
 * declares both, and the two holds compose here:
 *
 * - **No lock on the exit** — it gains one whose only hold is the
 *   obstruction, speaking the actor's `blockingNarratives`. A lock with no
 *   bolt opens the moment the way is clear (the engine's rule), so the exit
 *   is exactly as free as it was, except while the body is in it.
 * - **A lock already there** — its `blockedWhen` becomes `any_of` the old
 *   hold and the actor's (two bodies may stand in one doorway), and the
 *   actor's words join its `refusal` as the FIRST variant, keyed on the
 *   actor's presence, so the body speaks before the bolt does. The bolt
 *   (`lockedWhen`, and the absent-means-shut rule) is untouched.
 *
 * The actor keeps its `blocksExits` (the document is not mutated; only the
 * loaded pack's exits change), so the audit, the studio and a future
 * migration all still see what the author wrote. An exit named that no room
 * declares is left for `actor-validation` to refuse.
 *
 * @packageDocumentation
 */

import type {
	ActorDefinition,
	ConditionalNarratives,
	ConditionDefinition,
	ContentPackDefinition,
	ExitDefinition,
	ExitLock,
	SpokenWhen,
} from '@orpheus/content-schema';

/** The bodies standing in one doorway — at least one, in declared order. */
type Blockers = readonly [ActorDefinition, ...ActorDefinition[]];

/**
 * Every exit some actor blocks, with the actors blocking it in declared order.
 *
 * @param contentPack - The parsed pack
 * @returns The blockers per exit
 */
const blockersByExitIdOf = (contentPack: ContentPackDefinition): ReadonlyMap<string, Blockers> => {
	const blockers = new Map<string, Blockers>();

	for (const actor of contentPack.actors) {
		for (const exitId of actor.blocksExits) {
			const already = blockers.get(exitId);

			blockers.set(exitId, already === undefined ? [actor] : [...already, actor]);
		}
	}

	return blockers;
};

/**
 * The condition under which one body is in the way: it is PRESENT — in the
 * player's room, and visible — and, for a FOE (D244, keeping D234's word
 * that a fallen body is "never hostile or blocking again"), not yet felled.
 * A defeated body stays where it fell (D234), so presence alone would hold
 * the door for a corpse; until D244 the sugar said exactly that, and no
 * shipped pack had a foe in a doorway to notice.
 *
 * @param actor - The body standing in the way
 * @returns The one condition that holds the way shut while it stands there
 */
const presenceOf = (actor: ActorDefinition): ConditionDefinition => {
	const present: ConditionDefinition = { actorId: actor.actorId, type: 'actor_present' };

	return actor.combat === undefined ? present : { conditions: [present, { condition: { actorId: actor.actorId, type: 'actor_defeated' }, type: 'not' }], type: 'all_of' };
};

/**
 * One body's words, spoken while that body is the one in the way.
 *
 * The schema refuses `blocksExits` without `blockingNarratives` (the sugar
 * carries its own words), so an actor that reaches this pass always has
 * them; the `??` is the optional-property discipline's guard on an access
 * the runtime cannot miss — the same root cause the config's remarks name.
 */
const variantOf = (actor: ActorDefinition): SpokenWhen => ({
	/* v8 ignore next */
	narratives: actor.blockingNarratives ?? [],
	when: presenceOf(actor),
});

/**
 * The obstruction: the lock's own, if it had one, and every blocker's
 * presence — one condition alone, or all of them under `any_of`.
 *
 * @param existing - The lock's `blockedWhen` before, or nothing
 * @param blockers - The bodies standing in the doorway
 * @returns The one condition that holds the way shut
 */
const obstructionOf = (existing: ConditionDefinition | undefined, blockers: Blockers): ConditionDefinition => {
	const [first, ...rest] = blockers;
	const holds = [...(existing === undefined ? [] : [existing]), presenceOf(first), ...rest.map(presenceOf)];

	return existing === undefined && rest.length === 0 ? presenceOf(first) : { conditions: holds, type: 'any_of' };
};

/**
 * The refusal with each blocker's words added as leading variants, keyed on
 * that blocker's presence.
 *
 * @param existing - The lock's refusal before, or nothing when the exit had no lock
 * @param blockers - The bodies, in declared order
 * @returns The refusal that speaks for the body first and the bolt after
 */
const refusalWithBlockers = (existing: ConditionalNarratives | undefined, blockers: Blockers): ConditionalNarratives => {
	const [first, ...rest] = blockers;
	const blockerVariants: readonly SpokenWhen[] = [variantOf(first), ...rest.map(variantOf)];

	// A lock synthesized for the obstruction alone: one blocker's words ARE the
	// refusal; more blockers choose by presence, the first declared as fallback.
	if (existing === undefined) {
		return rest.length === 0 ? variantOf(first).narratives : { fallback: variantOf(first).narratives, variants: blockerVariants };
	}

	return 'variants' in existing
		? { fallback: existing.fallback, variants: [...blockerVariants, ...existing.variants]}
		: { fallback: existing, variants: blockerVariants };
};

/**
 * The exit's lock with the blockers' hold and words folded in.
 *
 * @param exit - The exit, as authored
 * @param blockers - The bodies standing in it
 * @returns The lock the loaded pack carries
 */
const lockWithBlockers = (exit: ExitDefinition, blockers: Blockers): ExitLock => ({
	...exit.lock,
	blockedWhen: obstructionOf(exit.lock?.blockedWhen, blockers),
	refusal: refusalWithBlockers(exit.lock?.refusal, blockers),
});

/**
 * The pack with every `blocksExits` rewritten into the named exits' locks.
 *
 * @param contentPack - The parsed pack
 * @returns The same pack, or one whose blocked exits carry the obstruction
 */
export const desugarActorBlocks = (contentPack: ContentPackDefinition): ContentPackDefinition => {
	const blockersByExitId = blockersByExitIdOf(contentPack);

	if (blockersByExitId.size === 0) {
		return contentPack;
	}

	return {
		...contentPack,
		rooms: contentPack.rooms.map((room) => ({
			...room,
			exits: room.exits.map((exit) => {
				const blockers = blockersByExitId.get(exit.exitId);

				return blockers === undefined ? exit : { ...exit, lock: lockWithBlockers(exit, blockers) };
			}),
		})),
	};
};
